import { Injectable } from "@angular/core";
import { HttpClient } from '@angular/common/http';
import { RuntimeConfigService } from './runtime-config.service';
import { BehaviorSubject, Observable, firstValueFrom } from "rxjs";
import { map } from "rxjs/operators";
import { HEADER_SLOT_NONE, MenuItem, MenuItemClickType, MenuItemType } from "../app/main/main-menu/menu-item/menu-item";
import { HybridStorageKeys, HybridStorageService } from "../app/services/hybrid-storage.service";
import { StudentInfo } from "../app/apps/student/models/StudentInfo";
import { VolunteerRecordStatus } from "../app/apps/student/models/VolunteerRecordStatus";
import { GuardianTodosResult } from "../app/apps/student/models/Todo";

@Injectable({
  providedIn: 'root'
})
export class MainMenuService {

  /**
   * How long a cached menu *structure* stays fresh. While fresh, returning to the main menu
   * reuses it and only the per-app badges refresh; once stale the whole menu is re-fetched.
   * Structural edits re-persist the cache, resetting the window.
   */
  private static readonly CACHE_MINUTES = 30;

  /** Nav-row slots after Home. Mirrors MainMenuService.MaxHeaderApps on the server. */
  static readonly MAX_HEADER_APPS = 3;

  private apiUrl: string;
  private guardianUrl: string;
  private studentUrl: string;

  // This service is the single owner of HybridStorageKeys.MainMenu_MenuItems. The main menu
  // grid and the header/footer nav row all read from menuItems$ so they can never disagree,
  // and ensureMenuItemsLoaded() dedupes their concurrent calls into one request.
  private readonly menuItemsSubject = new BehaviorSubject<MenuItem[] | null>(null);
  private hydrated = false;
  private inFlight: Promise<boolean> | null = null;
  private lastLoadedAtMs = 0;

  /** The user's full menu, or null before anything has loaded. */
  readonly menuItems$ = this.menuItemsSubject.asObservable();

  /** Apps in the header/footer nav row, in slot order. Home is hardcoded and is not an app. */
  readonly headerApps$: Observable<MenuItem[]> =
    this.menuItemsSubject.pipe(map(items => MainMenuService.pinnedApps(items)));

  // The grid tile currently being dragged in edit mode. The nav row (apps-header/apps-footer)
  // watches this to explain up front why it would refuse the tile — a rejected
  // cdkDropListEnterPredicate gives the user no feedback at all.
  private readonly draggingGridAppSubject = new BehaviorSubject<MenuItem | null>(null);
  readonly draggingGridApp$ = this.draggingGridAppSubject.asObservable();

  setDraggingGridApp(item: MenuItem | null): void {
    this.draggingGridAppSubject.next(item);
  }

  get menuItems(): MenuItem[] | null {
    return this.menuItemsSubject.value;
  }

  constructor(
    private http: HttpClient,
    private runtimeConfig: RuntimeConfigService,
    private hybridStorage: HybridStorageService
  ) {
    this.apiUrl = this.runtimeConfig.currentConfig.apiUrl + '/mainmenu';
    this.guardianUrl = this.runtimeConfig.currentConfig.apiUrl + '/guardian';
    this.studentUrl = this.runtimeConfig.currentConfig.apiUrl + '/Student';

    // Subscribe to config changes to update apiUrl
    this.runtimeConfig.config$.subscribe(config => {
      this.apiUrl = config.apiUrl + '/mainmenu';
      this.guardianUrl = config.apiUrl + '/guardian';
      this.studentUrl = config.apiUrl + '/Student';
    });
  }

  getMenuItemsByUserIdAsync(): Observable<MenuItem[]> {
    return this.http.get<MenuItem[]>(`${this.apiUrl}/GetMenuItemsByUserIdAsync/`);
  }

  private static pinnedApps(items: MenuItem[] | null): MenuItem[] {
    return (items ?? [])
      // >= 0 rather than != null: HEADER_SLOT_NONE is a pending removal, not a slot.
      .filter(i => i.ItemType === MenuItemType.App && (i.HeaderSortOrder ?? -1) >= 0)
      .sort((a, b) => (a.HeaderSortOrder! - b.HeaderSortOrder!) || (a.Id - b.Id));
  }

  /**
   * Whether an app may go in the nav row. It needs a monochrome icon, and External apps are
   * excluded because they need the auth-forwarding launch flow rather than a routerLink.
   */
  static canPinToHeader(item: MenuItem): boolean {
    return item.ItemType === MenuItemType.App
        && !!item.HeaderIconClass
        && item.ClickType !== MenuItemClickType.External;
  }

  /** Adds an app to the nav row at `index`, or moves it there if it's already in the row. */
  async pinAppToHeader(appId: number, index: number): Promise<void> {
    const items = this.menuItemsSubject.value;
    const app = items?.find(i => i.Id === appId && i.ItemType === MenuItemType.App);
    if (!items || !app || !MainMenuService.canPinToHeader(app)) {
      return;
    }

    // Excluding the app first makes this the same operation whether it's being added or moved.
    const row = MainMenuService.pinnedApps(items).filter(i => i.Id !== appId);
    if (row.length >= MainMenuService.MAX_HEADER_APPS) {
      return;
    }

    row.splice(Math.max(0, Math.min(index, row.length)), 0, app);
    row.forEach((item, slot) => item.HeaderSortOrder = slot);
    await this.saveMenuItems(items);
  }

  /** Removes an app from the nav row, leaving it on the main menu grid. */
  async unpinAppFromHeader(appId: number): Promise<void> {
    const items = this.menuItemsSubject.value;
    const app = items?.find(i => i.Id === appId);
    if (!items || !app || (app.HeaderSortOrder ?? -1) < 0) {
      return;
    }

    app.HeaderSortOrder = HEADER_SLOT_NONE;
    MainMenuService.pinnedApps(items).forEach((item, slot) => item.HeaderSortOrder = slot);
    await this.saveMenuItems(items);
  }

  /**
   * Posts the whole menu and refreshes the cache. Always the whole list: the server renumbers
   * nav-row slots densely over whatever it receives, so a partial post would reshuffle the row.
   */
  async saveMenuItems(items: MenuItem[]): Promise<void> {
    // Publish before the request so the edit shows immediately instead of after the round trip.
    this.menuItemsSubject.next(items);
    await firstValueFrom(this.updateMenuItemsAsync(items));
    await this.setMenuItems(items);
  }

  /**
   * Ensures menuItems$ holds data, fetching only when the cache is missing or stale. Concurrent
   * callers (the main menu, the header and the footer all call this) share one request.
   * @returns true when the value came from a still-fresh cache, so the caller can refresh just
   * the badges instead of the whole structure.
   * @throws the HttpErrorResponse when a needed fetch fails and there is nothing cached.
   */
  async ensureMenuItemsLoaded(): Promise<boolean> {
    if (this.inFlight) {
      return this.inFlight;
    }

    this.inFlight = this.loadMenuItems();
    try {
      return await this.inFlight;
    } finally {
      this.inFlight = null;
    }
  }

  private async loadMenuItems(): Promise<boolean> {
    let ageMs = Infinity;

    if (!this.hydrated) {
      this.hydrated = true;
      // Age-blind read so the menu paints immediately, then the age decides whether to refetch.
      // Deliberately not getItemIfFresh, which deletes the entry when stale — we still want to
      // show it while the refresh is in flight.
      const { data, lastUpdated } = await this.hybridStorage
        .getItemWithTimestamp<MenuItem[]>(HybridStorageKeys.MainMenu_MenuItems);
      if (data) {
        this.menuItemsSubject.next(data);
        ageMs = lastUpdated ? Date.now() - lastUpdated : Infinity;
      }
    } else if (this.lastLoadedAtMs) {
      ageMs = Date.now() - this.lastLoadedAtMs;
    }

    if (ageMs <= MainMenuService.CACHE_MINUTES * 60_000) {
      return true;
    }

    try {
      await this.setMenuItems(await firstValueFrom(this.getMenuItemsByUserIdAsync()));
      return false;
    } catch (error) {
      // A stale cache still beats an empty menu, so only surface the failure when we have
      // nothing to show.
      if (this.menuItemsSubject.value) {
        console.error('MainMenuService: menu refresh failed, keeping cached menu', error);
        return true;
      }
      throw error;
    }
  }

  /** Publishes a menu and writes it to the shared cache, resetting the freshness window. */
  async setMenuItems(items: MenuItem[]): Promise<void> {
    this.hydrated = true;
    this.lastLoadedAtMs = Date.now();
    this.menuItemsSubject.next(items);
    await this.hybridStorage.setItem(HybridStorageKeys.MainMenu_MenuItems, items);
  }

  /**
   * Forces a server refetch while keeping the current menu visible — used after a language
   * change, since app titles arrive translated from the server and the cached copy goes stale.
   */
  async refreshMenuItems(): Promise<void> {
    try {
      await this.setMenuItems(await firstValueFrom(this.getMenuItemsByUserIdAsync()));
    } catch (error) {
      console.error('MainMenuService: menu refresh after language change failed', error);
    }
  }

  /** Drops the in-memory menu. Impersonation also wipes the cache key via hybrid storage. */
  clearCaches(): void {
    this.menuItemsSubject.next(null);
    this.hydrated = false;
    this.lastLoadedAtMs = 0;
    this.inFlight = null;
  }

  getParentPortalsStudentsByGuardianAsync(): Observable<StudentInfo[]> {
    return this.http.get<StudentInfo[]>(`${this.guardianUrl}/GetStudentsByGuardianAsync`);
  }

  /**
   * The ParentPortal record for a logged-in student themselves — the same payload a guardian
   * gets for that student, so the main menu can gate student-only tiles (College Readiness) on
   * the portal's eligibility flags. No identity is sent beyond the user id: the server resolves
   * the caller from the auth token and only releases a student they're entitled to.
   */
  getOwnParentPortalStudentAsync(studentUserId: number): Observable<StudentInfo | null> {
    return this.http.get<StudentInfo | null>(
      `${this.studentUrl}/GetParentPortalStudentByUserIdAsync/${studentUserId}`);
  }

  /**
   * Whether the current guardian already has a volunteer record for the school's current
   * academic year (the server resolves the year). schoolId is the local Core.Schools Id.
   */
  getVolunteerRecordStatusAsync(schoolId: number): Observable<VolunteerRecordStatus> {
    return this.http.get<VolunteerRecordStatus>(`${this.guardianUrl}/GetVolunteerRecordStatusAsync/${schoolId}`);
  }

  /**
   * The guardian's Parent Portal home-page to-dos (form completion + Voice of the Parent),
   * already filtered, de-duped and ordered server-side (VOP todos first).
   */
  getTodosByGuardianAsync(): Observable<GuardianTodosResult> {
    return this.http.get<GuardianTodosResult>(`${this.guardianUrl}/GetTodosByGuardianAsync`);
  }

  /**
   * The current guardian's student user ids whose school has SPARK enabled — used by the
   * student page header switcher to disable students without SPARK while on the SPARK page.
   */
  getSparkEnabledStudentUserIdsAsync(): Observable<number[]> {
    return this.http.get<number[]>(`${this.apiUrl}/GetSparkEnabledStudentUserIdsAsync/`);
  }

  /**
   * Per-app notification badge counts keyed by AppId (as a string key in JSON).
   * Used to refresh app-icon badges without re-fetching the whole menu structure.
   */
  getAppNotificationCounts(): Observable<Record<number, number>> {
    return this.http.get<Record<number, number>>(`${this.apiUrl}/GetAppNotificationCountsAsync/`);
  }

  /**
   * Callers must pass the COMPLETE menu. The server renumbers nav-row slots densely over
   * whatever it receives, so a partial post would rewrite the posted app to slot 0 and collide
   * with the rest of the row. Prefer saveMenuItems().
   */
  updateMenuItemsAsync(menuItems: MenuItem[]): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/UpdateMenuItemsAsync`, menuItems);
  }
}
